/**
 * 🎨 UI Design Tokens
 *
 * НЕ-цветовые константы интерфейса (legacy + shared dimensions).
 * Цвета и v2-шкала — в v2-tokens.ts (SSOT редизайна).
 */

import { v2Tokens } from './v2-tokens';

export const uiTokens = {
  // ============================================
  // 📏 SPACING (из theme.ts)
  // ============================================

  spacing: {
    2: '2px',
    4: '4px',
    6: '6px',
    8: '8px',
    10: '10px',
    12: '12px',
    14: '14px',
    16: '16px',
    20: '20px',
    24: '24px',
    32: '32px',
    40: '40px',
    48: '48px',
    80: '80px',
  },

  // ============================================
  // 📐 DIMENSIONS (из theme.ts)
  // ============================================

  dimensions: {
    timelineColumn: '80px',
    /** Вторая колонка пересекающихся записей: смещение и ширина относительно сетки (UI Kit) */
    timelineOverlapLayerLeft: '40%',
    timelineOverlapLayerWidth: '60%',
    dropdownMaxHeight: '300px',
    timelineCardMinWidth: '140px',
    timelineCardMaxWidth: '220px',
    /** Ширина поля количества в строке товара (создание/редактирование записи) */
    quantityInputWidth: '56px',
  },

  // ============================================
  // 🔲 BORDER RADIUS (из theme.ts)
  // ============================================

  radius: {
    4: '4px',
    8: '8px',
    10: '10px',
    12: '12px',
    14: '14px',
    16: '16px',
    /** v2 redesign — SSOT: v2-tokens.ts */
    20: v2Tokens.radius.sheet,
    22: v2Tokens.radius.frame,
    pill: v2Tokens.radius.pill,
  },

  // ============================================
  // 📊 BORDER WIDTH (из theme.ts)
  // ============================================

  borderWidth: {
    thin: '1px',
    normal: '2px',
  },

  // ============================================
  // 🔤 TYPOGRAPHY (из theme.ts + design-config.ts)
  // ============================================

  typography: {
    fontSize: {
      micro: '11px',  // роль micro
      xs: '12px',     // роль meta
      sm: '14px',     // роль body
      md: '16px',     // роль title
      lg: '18px',
      xl: '20px',
    },

    // Font weights (соответствуют требованиям: regular=400, medium=500, semibold=600, bold=700)
    fontWeight: {
      regular: 400,   // font-regular
      normal: 400,    // alias для обратной совместимости
      medium: 500,    // font-medium
      semibold: 600,  // font-semibold
      bold: 700,      // font-bold
    },

    // Line heights (соответствуют требованиям: для каждого размера)
    lineHeight: {
      tight: 1.2,    // для заголовков (xs: 14.4, sm: 16.8, md: 19.2, lg: 21.6, xl: 24)
      normal: 1.4,   // для основного текста (xs: 16.8, sm: 19.6, md: 22.4, lg: 25.2, xl: 28)
      relaxed: 1.6,  // для длинных текстов (xs: 19.2, sm: 22.4, md: 25.6, lg: 28.8, xl: 32)
    },

    // Основной шрифт приложения - Inter с минимальным fallback (из design-config)
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  // ============================================
  // 📐 ELEVATION/SHADOW (из theme.ts)
  // ============================================

  elevation: {
    none: 'none',
    sm: '0 1px 1px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    md: '0 2px 2px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
    /** Нижний таббар — тень вверх, контраст на светлом фоне */
    tabBar: '0 -4px 12px rgba(0,0,0,0.12), 0 -2px 4px rgba(0,0,0,0.06)',
    lg: '0 4px 4px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.06)',
    xl: '0 8px 8px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.06)',
    '2xl': '0 12px 12px rgba(0,0,0,0.10), 0 12px 24px rgba(0,0,0,0.08)',
    /** v2 redesign — SSOT: v2-tokens.ts */
    float: v2Tokens.elevation.float,
    sheet: v2Tokens.elevation.sheet,
  },

  // ============================================
  // ⏱️ DURATIONS (из theme.ts)
  // ============================================

  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '250ms',
    slow: '350ms',
  },

  // ============================================
  // 🎛️ CONTROL SIZES (из design-config.ts)
  // ============================================

  control: {
    // Высота для форм (Input, Select, Textarea)
    formHeight: '48px',

    // Высота для компактных зон (topbar, inline)
    compactHeight: '40px',

    /** Минимальная высота зоны нажатия для тач-устройств (a11y, Apple HIG / Material) */
    touchTargetMin: '44px',

    // Размеры выпадающих списков (Select)
    select: {
      sm: '36px',  // Компактный размер
      md: '48px',  // Стандартный размер (по умолчанию)
    },

    // Размеры кнопок
    button: {
      sm: {
        height: '40px',
        paddingX: '12px',
        paddingY: '8px',
      },
      md: {
        height: '48px',
        paddingX: '16px',
        paddingY: '10px',
      },
    },

    // Размеры иконок-кнопок
    iconButton: {
      sm: {
        size: '32px',
        iconSize: 16,
      },
      md: {
        size: '40px',
        iconSize: 20,
      },
      lg: {
        size: '48px',
        iconSize: 24,
      },
    },

    // Размеры поиска в топбаре
    search: {
      height: '40px',
      paddingX: '12px',
      paddingXWithIcon: '36px', // padding-left когда есть иконка
      iconSize: 16,
      iconOffset: '12px', // отступ иконки слева
    },
  },

  // ============================================
  // 🪟 MODAL (из design-config.ts)
  // ============================================

  modal: {
    topOffset: '24px',      // Отступ сверху для align='top'
    sideMargin: '16px',      // Отступы слева и справа (использует spacing[16])
    bottomMargin: '16px',    // Отступ снизу (использует spacing[16])
    maxWidth: '360px',       // Максимальная ширина модалки
    // backdropFilter убран - используется effects.modalBackdropFilter
  },

  // ============================================
  // 📋 LISTS (из design-config.ts)
  // ============================================

  list: {
    // Отступы элементов списка (использует spacing[12])
    itemPadding: '12px', // TODO: заменить на spacing[12]
  },

  // ============================================
  // 🔀 SEGMENTED CONTROL (из design-config.ts)
  // ============================================

  segmented: {
    // Фон контейнера (подложка) - теперь через CSS vars для поддержки тем
    containerBg: 'var(--color-surface)',

    // Цвета активных элементов для тематических статусов берутся из appointment.bgHeader*
    // (единый источник правды - см. appointment.bgHeader*)
    // Для обычных переключателей (не тематических) используется activeDefault

    // Дефолтный цвет для активного элемента (чистый белый)
    activeDefault: '#FFFFFF',

    // Размеры для SegmentedToggle
    height: {
      sm: '28px',
      md: '36px',
      lg: '48px',
    },
    paddingX: {
      sm: '8px',
      md: '12px',
      lg: '16px',
    },
    radius: {
      sm: '10px',
      md: '10px',
      lg: '10px',
    },
    gap: '4px', // Отступ между сегментами

    // Цвета
    pillBg: 'transparent', // Фон неактивного сегмента
    pillBgActive: '#FFFFFF', // Фон активного сегмента (default variant)
    text: 'var(--color-text-muted)', // Текст неактивного сегмента
    textActive: 'var(--color-text)', // Текст активного сегмента (default variant)
  },

  // ============================================
  // 📐 LAYOUT DIMENSIONS (из design-config.ts)
  // ============================================

  layout: {
    // Высота header
    headerHeight: '64px',
    // Высота нижних табов
    tabsHeight: '64px',
    // Padding для контента (чтобы не перекрывался с табами)
    contentBottomPadding: '64px',
    // Максимальная ширина приложения (desktop preview)
    maxWidth: '480px',
  },

  // ============================================
  // 📱 SCREEN LAYOUT (из design-config.ts)
  // ============================================

  screen: {
    // Максимальная ширина экрана
    maxWidth: '480px',
    // Боковые отступы
    sidePadding: '16px',
    // Отступ сверху (высота header)
    topOffset: '64px',
    // Отступ снизу (высота tabs)
    bottomOffset: '64px',
    // Радиус поверхности
    radius: '16px',
    // Граница поверхности (или none)
    border: 'none',
    // Тень берётся из elevation.lg через CSS переменную var(--elevation-lg)
  },

  // ============================================
  // 📚 Z-INDEX (из design-config.ts)
  // ============================================

  zIndex: {
    base: 0,      // Базовый слой
    content: 1,   // Контент
    bars: 10,     // Верхний/нижний бар
    overlay: 20,  // Затемнение
    onboarding: 25, // Overlay и tooltip тура (выше overlay, ниже modal)
    modal: 30,    // Модалки
    confirm: 40,  // Подтверждения
  },

  // ============================================
  // 🎬 EFFECTS (из design-config.ts)
  // ============================================

  effects: {
    /** Размытие фона для модалок - минимальный blur для видимости контента */
    modalBackdropFilter: 'blur(6px) saturate(130%)',
    /** Размытие фона для overlay — лёгкая матовость */
    overlayBackdropFilter: 'blur(6px) saturate(130%)',
  },

  // ============================================
  // 📝 INPUT MASKS (стандарты индустрии)
  // ============================================

  inputMasks: {
    // Маска для времени (HH:MM - латиница, стандарт индустрии)
    time: 'HH:MM',
    // Маски для даты (DD.MM.YYYY - латиница, стандарт индустрии)
    date: {
      day: 'DD',
      month: 'MM',
      year: 'YYYY',
    },
  },

  // ============================================
  // 🎨 DISABLED STATE COLORS (цвета disabled состояния)
  // ============================================

  disabled: {
    // Фон disabled полей - нейтральный серый, адаптированный к теме
    background: 'color-mix(in srgb, var(--c-surface) 90%, #F0F0F0 10%)', // 90% surface + 10% нейтральный серый

    // Граница disabled полей - полупрозрачная (70% border + 30% transparent)
    border: 'color-mix(in srgb, var(--c-border) 70%, transparent 30%)',

    // Цвет текста для disabled полей
    text: 'var(--color-text-placeholder)',

    // Прозрачность disabled состояния для интерактивных контролов
    opacity: '0.6',

    // Отступы и размеры disabled полей наследуются от control
  },
} as const;
